<template>
	<div>
		<v-data-table
			:items="groupedLicenses"
			:items-per-page="-1"
			dense
			:headers="headers"
			:height="pageHeight(174)"
			item-key="organizationId"
			sort-by="organizationId"
			ref="table"
			:loading="loading"
			:no-data-text="$t('no-data-available')"
			:loading-text="$t('loading')">
			<template #item="{ item, index, expand, isExpanded }">
				<tr :class="[getRowsForDataTable(index), { 'expanded-row': isExpanded }]" @click="expand(!isExpanded)">
					<td class="text-left d-flex align-center">
						<v-icon small color="primary" class="mr-1">
							{{ isExpanded ? 'mdi-arrow-down-drop-circle' : 'mdi-arrow-right-drop-circle' }}
						</v-icon>
						{{ getInstitutionName(item.organizationId) }}
					</td>
					<td v-if="roleName === 'SYSTEM_ADMIN'" class="text-left">
						<span class="mr-3 font-weight-bold font-size12">
							<v-icon class="mr-1" size="15" color="green">mdi-checkbox-blank-circle</v-icon>
							{{ $t('active-licenses-total') }}: {{ licensesForSystemAdmin(item.licenses).active }}
						</span>
						<span class="mx-3 font-weight-bold font-size12">
							<v-icon class="mr-1" size="15" color="gray">mdi-checkbox-blank-circle</v-icon>
							{{ $t('inactive-licenses') }}: {{ licensesForSystemAdmin(item.licenses).inactive }}
						</span>
						<span class="mx-3 font-weight-bold font-size12">
							<v-icon class="mr-1" size="15" color="primary">mdi-checkbox-blank-circle</v-icon>
							{{ $t('assigned-licenses') }}: {{ licensesForSystemAdmin(item.licenses).assigned }}
						</span>
						<span class="mx-3 font-weight-bold font-size12">
							<v-icon class="mr-1" size="15" color="gray">mdi-checkbox-blank-circle</v-icon>
							{{ $t('not-assigned-licenses') }}: {{ licensesForSystemAdmin(item.licenses).notAssigned }}
						</span>
						<span class="ml-3 font-weight-bold font-size12">
							<v-icon class="mr-1" size="15" color="blue">mdi-checkbox-blank-circle</v-icon>
							{{ $t('total') }}:
							{{ licensesForSystemAdmin(item.licenses).total }}
						</span>
					</td>
				</tr>
			</template>
			<template #expanded-item="{ item }">
				<tr style="cursor: default; background-color: #fff !important">
					<td class="pa-3" :colspan="headers.length">
						<v-data-table
							:items="item.licenses"
							:headers="insideHeaders"
							dense
							:items-per-page="-1"
							hide-default-footer
							class="data-table-outline"
							:height="pageHeight(95)"
							fixed-header>
							<template #item="{ item, index }">
								<tr :class="getRowsForDataTable(index)" class="font-size12-5 font-weight-medium billings-border-bottom">
									<td class="text-left font-weight-bold">
										<v-icon small :style="item.isActive ? 'color: #09CB09' : ''">mdi-checkbox-blank-circle</v-icon>
										{{ item.licenseName }}
									</td>
									<td class="text-left">
										{{ item.patientName }}
									</td>
									<td class="text-left">
										{{ item.officeName }}
									</td>
									<td class="text-left">
										{{ item.licenseDescription }}
									</td>
									<td class="text-left">
										{{ item.startDate !== null ? formattedDateTime(item.startDate, 'date_med') : '' }}
									</td>
									<td class="text-left">
										{{ item.expirationDate !== null ? formattedDateTime(item.expirationDate, 'date_med') : '' }}
									</td>
									<td
										v-if="
											item.isLicenseAssigned &&
											item.isActive &&
											['DOCTOR', 'NURSE', 'HOSPITAL_ADMIN'].includes(roleName)
										"
										class="text-left">
										<v-btn
											@click.stop="openDeassignDialog(item)"
											min-width="120"
											max-width="120"
											color="primary"
											x-small>
											{{ $t('deassign-license') }}
										</v-btn>
									</td>
									<td
										v-else-if="
											!item.isLicenseAssigned &&
											item.isActive &&
											['DOCTOR', 'NURSE', 'HOSPITAL_ADMIN'].includes(roleName)
										"
										class="text-left">
										<v-btn
											@click.stop="syncronizeDrawers(item)"
											min-width="120"
											max-width="120"
											class="white--text"
											x-small
											color="green">
											{{ $t('assign-licence') }}
										</v-btn>
									</td>
									<td v-else-if="['DOCTOR', 'NURSE', 'HOSPITAL_ADMIN'].includes(roleName)" />
									<td class="text-right">
										{{ item.price }}
										<v-tooltip v-if="roleName === 'SYSTEM_ADMIN'" color="primary" left>
											<template v-slot:activator="{ on, attrs }">
												<v-icon v-on="on" v-bind="attrs" size="20" @click.stop="openDeleteDialog(item)" color="primary">
													mdi-delete
												</v-icon>
											</template>
											<span>{{ $t('delete-license') }}</span>
										</v-tooltip>
									</td>
								</tr>
							</template>
						</v-data-table>
					</td>
				</tr>
			</template>
			<template v-slot:[`footer.prepend`]>
				<span class="mx-3 font-weight-bold">{{ $t('table-legend') }}:</span>
				<span class="mx-2 font-weight-bold">
					<v-icon class="mr-1" size="15" color="green">mdi-checkbox-blank-circle </v-icon>
					{{ $t('active-licenses-total') }}: {{ licenseStats.active }}
				</span>
				<span class="mx-2 font-weight-bold">
					<v-icon class="mr-1" size="15" color="gray">mdi-checkbox-blank-circle</v-icon>
					{{ $t('inactive-licenses') }}: {{ licenseStats.inactive }}
				</span>
				<span class="mx-2 font-weight-bold">
					<v-icon class="mr-1" size="15" color="primary">mdi-checkbox-blank-circle </v-icon>
					{{ $t('assigned-licenses') }}: {{ licenseStats.assigned }}
				</span>
				<span class="mx-2 font-weight-bold">
					<v-icon class="mr-1" size="15" color="gray">mdi-checkbox-blank-circle</v-icon>
					{{ $t('not-assigned-licenses') }}: {{ licenseStats.notAssigned }}
				</span>
				<span class="mx-2 font-weight-bold">
					<v-icon class="mr-1" size="15" color="blue">mdi-checkbox-blank-circle</v-icon>
					{{ $t('total') }}:
					{{ licenseStats.total }}
				</span>
			</template>
		</v-data-table>
		<DeleteDialog
			:dialog.sync="showDeleteDialog"
			@confirm="
				showDeleteDialog = false;
				deleteLicense();
			" />
		<DeAssignLicenseConfirmationDialog
			:patientDialog="patientDialog"
			:licenseId="licenseId"
			:patientName="patientName"
			@update:patientDialog="patientDialog = $event"
			v-if="patientDialog" />
	</div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import DeAssignLicenseConfirmationDialog from '@/views/dialogsNew/DeAssignLicenseConfirmationDialog.vue';
import { showErrorAlert, toggleHeaders, getRowsForDataTable } from '@/utils/utilities';
import { formattedDateTime } from '@/utils/luxon-formater';
import DeleteDialog from '@/views/dialogs/DeleteDialog.vue';

export default {
	name: 'Licenses.vue',
	components: {
		DeAssignLicenseConfirmationDialog,
		DeleteDialog,
	},
	data () {
		return {
			loading: false,
			showDeleteDialog: false,
			licenseToDelete: null,
			patientDialog: false,
			licenseId: null,
			patientName: null,
		};
	},
	methods: {
		getRowsForDataTable,
		toggleHeaders,
		formattedDateTime,
		licensesForSystemAdmin (item) {
			const stats = {
				assigned: 0,
				notAssigned: 0,
				active: 0,
				inactive: 0,
				total: 0,
			};
			item.forEach((license) => {
				if (license.isActive) {
					if (license.isLicenseAssigned) stats.assigned++;
					else stats.notAssigned++;
				}
				if (license.isActive) stats.active++;
				else stats.inactive++;
			});
			stats.total = stats.active + stats.inactive;
			item.stats = stats;
			return stats;
		},
		openDeassignDialog (item) {
			this.patientDialog = true;
			this.patientName = item.patientName;
			this.licenseId = item.activeLicenseAssignmentId;
		},
		syncronizeDrawers (item) {
			var parameters = {};
			parameters = {
				detailsDrawer: true,
				page: 'assignPatientLicense',
				item: item,
			};
			this.$store.commit('SET_DETAILS_DRAWER', parameters);
		},
		getOddRows (index) {
			if (index % 2 === 1) {
				return 'odd-row';
			}
			if (index % 2 === 0) {
				return 'even-row';
			}
		},
		openDeleteDialog (item) {
			this.showDeleteDialog = true;
			this.licenseToDelete = item;
		},
		openViewDrawer (item) {
			const body = {
				id: item.id,
			};
			this.$store.dispatch('licenses/getLicenseForOrganization', body).then((res) => {
				this.$store.commit('SET_EDIT_DETAILS_DRAWER', true);
			});
		},
		getLicenses () {
			this.loading = true;
			this.$store.dispatch('licenses/getLicenses').then((res) => {
				this.toggleHeaders();
				this.loading = false;
			});
		},
		getInstitutionName (val) {
			const data = this.medicalInstitutions?.find((institution) => institution.id === val)?.institutionName;
			if (data) {
				return data;
			} else {
				return this.$t('loading');
			}
		},
		async deleteLicense () {
			try {
				const body = {
					id: this.licenseToDelete.id,
				};
				const response = await this.$store.dispatch('licenses/deleteLicense', body);

				if (response.resFlag && response.statusCode === 200 && response.data) {
					const deletedId = response.data.id;

					const index = this.licenses.findIndex((licence) => licence.id === deletedId);

					if (index !== -1) {
						this.licenses.splice(index, 1);
					}
				}
			} catch (error) {
				showErrorAlert(error.response.data.msg);
			}
		},
	},
	computed: {
		...mapState({
			licenses: (state) => state.licenses.licenses,
		}),
		...mapGetters({
			pageHeight: 'pageHeight',
			appBarSearch: 'filterbar/getSearch',
			appBarLicense: 'filterbar/getLicensesStatus',
			medicalInstitutions: 'medicalInstitutions/getMedicalInstitutionsSorted',
			roleName: 'authentication/getRole',
		}),

		licenseStats () {
			const stats = {
				assigned: 0,
				notAssigned: 0,
				active: 0,
				inactive: 0,
				total: 0,
			};
			this.licenses.forEach((item) => {
				if (item.isLicenseAssigned) stats.assigned++;
				else stats.notAssigned++;
				if (item.isActive) stats.active++;
				else stats.inactive++;
			});
			stats.total = stats.active + stats.inactive;
			return stats;
		},
		getFilteredLicenses () {
			if (this.appBarLicense !== null) {
				const activeLicenses = this.licenses.filter((license) => license.isActive === this.appBarLicense);
				return activeLicenses;
			}
			return this.licenses;
		},
		groupedLicenses () {
			return this.getFilteredLicenses.reduce((groups, license) => {
				const term = (this.appBarSearch || '').toLowerCase();
				if (
					this.appBarSearch !== null &&
					this.appBarSearch !== '' &&
					!license.licenseName?.toLowerCase().includes(term) &&
					(!license.patientName || !license.patientName.toLowerCase().includes(term)) &&
					(!license.officeName || !license.officeName.toLowerCase().includes(term)) &&
					(!license.licenseDescription || !license.licenseDescription.toLowerCase().includes(term)) &&
					!this.getInstitutionName(license.organizationId).toLowerCase().includes(term)
				) {
					return groups;
				}
				let group = groups.find((g) => g.organizationId === license.organizationId);
				if (!group) {
					group = { organizationId: license.organizationId, licenses: [] };
					groups.push(group);
				}
				group.licenses.push(license);
				return groups;
			}, []);
		},
		headers () {
			let headers = [{ text: this.$t('organization-name'), value: 'organizationId', align: 'start' }];
			if (this.roleName === 'SYSTEM_ADMIN') {
				headers = [...headers, { text: '', value: '', align: 'start' }];
			}
			return headers;
		},
		insideHeaders () {
			let headers = [
				{
					text: this.$t('license-name'),
					value: 'licenseName',
					align: 'start',
					id: 1,
				},
				{
					text: this.$t('patient-name'),
					align: 'start',
					value: 'patientName',
					id: 2,
				},
				{
					text: this.$t('location-name'),
					align: 'start',
					value: 'officeName',
					id: 3,
				},
				{
					text: this.$t('license-description'),
					align: 'start',
					value: 'licenseDescription',
					id: 4,
				},
				{
					text: this.$t('start-date'),
					align: 'start',
					sortable: false,
					id: 5,
				},
				{
					text: this.$t('expiration-date'),
					align: 'start',
					sortable: false,
					id: 6,
				},
				{
					text: this.$t('is-license-assigned'),
					align: 'start',
					sortable: false,
					id: 7,
				},
				{
					text: this.$t('price'),
					align: 'end',
					value: 'price',
					sortable: false,
					id: 8,
				},
			];

			if (this.roleName === 'SYSTEM_ADMIN') {
				headers = headers.filter((header) => header.id !== 7);
			}
			if (this.roleName === 'ORGANIZATION_ADMIN') {
				headers = headers.filter((header) => header.id !== 7);
			}
			return headers;
		},
	},
	mounted () {
		this.getLicenses();
		if (this.roleName === 'HOSPITAL_ADMIN' || this.roleName === 'DOCTOR' || this.roleName === 'NURSE') {
			this.$store.dispatch('medicalInstitutions/getMedicalInstitutions');
		}
	},
};
</script>
